/**
 * OriginTenantBadge.
 *
 * Shown on federated work so vendor staff always know which origin tenant
 * sent the work. We deliberately DO NOT expose the origin tenant's
 * proprietary data beyond what the federation link's `event_visibility_config`
 * whitelists — this badge is a label, not a drill-through.
 *
 * The federation link's human `label` (e.g. "SKDLA — Bayside", a tenant-set
 * override) is the preferred display; the canonical originating lab name
 * (`partnerTenantName`) is the next-best. Only when neither is populated do we
 * fall back to a short, non-scary tenant tag rather than dumping a raw UUID at
 * the user.
 */
export interface OriginTenantBadgeProps {
  partnerTenantId: string;
  label?: string | null;
  /** Canonical name of the originating lab/tenant (resolver-provided). */
  partnerTenantName?: string | null;
}

function shortTenant(id: string): string {
  // UUID → "Tenant 2a2a" (stable, readable, not a wall of hex).
  const head = id.replace(/-/g, "").slice(0, 4);
  return `Tenant ${head}`;
}

function firstNonEmpty(...vals: Array<string | null | undefined>): string | null {
  for (const v of vals) {
    if (typeof v === "string" && v.trim() !== "") return v.trim();
  }
  return null;
}

export function OriginTenantBadge({
  partnerTenantId,
  label,
  partnerTenantName,
}: OriginTenantBadgeProps) {
  const resolved = firstNonEmpty(label, partnerTenantName);
  const display = resolved ?? shortTenant(partnerTenantId);
  // Show the resolved name in the tooltip alongside the raw id for support,
  // rather than leading with the UUID.
  const title = resolved
    ? `Origin lab: ${resolved} (${partnerTenantId})`
    : `Origin tenant id: ${partnerTenantId}`;
  return (
    <span className="origin-tenant-badge" title={title}>
      <span aria-hidden="true">◈</span>
      <span>{display}</span>
    </span>
  );
}
